import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Apple as AppleIcon,
  Computer as WindowsIcon,
  Storage as LinuxIcon,
} from '@mui/icons-material';
import { proxyService } from '../services/proxyService';
import { apiService, extractApiErrorMessage } from '../services/apiService';
import { useProxyConnection } from '../context/ProxyConnectionContext';
import { WINDOWS_PROXY_INSTALLER_VERSION } from '../utils/proxyAgentDownload';
import {
  msToTimeoutDisplay,
  timeoutInputToMs,
  type RequestTimeoutUnit,
} from '../utils/requestTimeout';
import { DFT_BRAND } from '../styles/brandPalette';

const PROXY_DOWNLOAD_BTN_SX = {
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '0.875rem',
  minWidth: 0,
  flex: 1,
  maxWidth: '100%',
  justifyContent: 'flex-start',
  whiteSpace: 'normal',
  textAlign: 'left' as const,
  lineHeight: 1.35,
  py: 0.75,
  minHeight: 36,
  borderRadius: '10px',
} as const;
const proxyBtnSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '0.875rem',
  height: 36,
  minHeight: 36,
  whiteSpace: 'nowrap' as const,
  boxShadow: 'none',
};
const proxyCardSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  border: `1px solid ${DFT_BRAND.line}`,
  borderRadius: '14px !important',
  boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
  bgcolor: DFT_BRAND.paper,
} as const;

/** Viewport-anchored Select menu — avoids CSS-zoom misalignment on tenant/server layouts. */
function useZoomSafeSelectMenu() {
  const [menuState, setMenuState] = useState({
    open: false,
    top: 0,
    left: 0,
    width: 0,
  });
  const anchorElRef = useRef<HTMLElement | null>(null);

  const measureFromEl = (combobox: HTMLElement) => {
    const rect = combobox.getBoundingClientRect();
    return {
      top: Math.round(rect.bottom + 4),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
    };
  };

  const onOpen = (e: React.SyntheticEvent) => {
    const el = e.currentTarget as HTMLElement;
    const combobox =
      (el.matches('[role="combobox"]')
        ? el
        : el.querySelector<HTMLElement>('[role="combobox"]')) ?? el;
    anchorElRef.current = combobox;
    const measured = measureFromEl(combobox);
    flushSync(() => {
      setMenuState({ open: true, ...measured });
    });
  };

  const onClose = () => setMenuState((prev) => ({ ...prev, open: false }));

  const remeasureWhileOpen = () => {
    const combobox = anchorElRef.current;
    if (!combobox) return;
    const measured = measureFromEl(combobox);
    setMenuState((prev) => (prev.open ? { ...prev, ...measured } : prev));
  };

  const MenuProps = {
    disableScrollLock: true,
    anchorReference: 'anchorPosition' as const,
    anchorPosition: { top: menuState.top, left: menuState.left },
    transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
    sx: { zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal },
    TransitionProps: {
      onEntering: remeasureWhileOpen,
      onEntered: remeasureWhileOpen,
    },
    PaperProps: {
      sx: {
        mt: 0.5,
        zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal,
        ...(menuState.width > 0
          ? {
              width: menuState.width,
              minWidth: menuState.width,
              maxWidth: 'min(100vw - 16px, 560px)',
            }
          : {}),
      },
    },
    MenuListProps: {
      sx: {
        maxHeight: 320,
        overflowY: 'auto' as const,
        py: 0.5,
      },
    },
  };

  return { menuOpen: menuState.open, onOpen, onClose, MenuProps };
}

const proxyDialogPaperSx = {
  width: { xs: 'calc(100% - 32px)', sm: 440 },
  maxWidth: 'min(92vw, 520px)',
  m: 2,
  borderRadius: '14px',
  border: `1px solid ${DFT_BRAND.line}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
} as const;

export interface ProxySettingsContentProps {
  /** When true, loads current settings and tests the proxy connection. */
  active?: boolean;
  /** Called after a successful save & test (e.g. close dialog). */
  onSaveSuccess?: () => void;
  /** Show Cancel + Save & Test (dialog) or only Save & Test (page). */
  footer?: 'dialog' | 'page' | 'none';
  onCancel?: () => void;
  /** Hide the page/dialog title row when an outer shell provides it. */
  showHeader?: boolean;
}

const ProxySettingsContent: React.FC<ProxySettingsContentProps> = ({
  active = true,
  onSaveSuccess,
  footer = 'page',
  onCancel,
  showHeader = true,
}) => {
  const { refreshProxyStatus } = useProxyConnection();
  const [proxyMode, setProxyMode] = useState<'auto' | 'custom'>('custom');
  const [customProxyUrl, setCustomProxyUrl] = useState('http://localhost:9000');
  const [proxyStatus, setProxyStatus] = useState<'checking' | 'available' | 'unavailable'>('checking');
  const [statusMessage, setStatusMessage] = useState('');
  const [testInProgress, setTestInProgress] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [currentInstructions, setCurrentInstructions] = useState({ title: '', content: '' });
  const [isEditingCustomProxy, setIsEditingCustomProxy] = useState(false);
  const [requestTimeoutValue, setRequestTimeoutValue] = useState('30');
  const [requestTimeoutUnit, setRequestTimeoutUnit] = useState<RequestTimeoutUnit>('seconds');
  const [requestTimeoutError, setRequestTimeoutError] = useState('');
  const timeoutUnitZoomMenu = useZoomSafeSelectMenu();

  const isLocalhostEnv =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.endsWith('.localhost'));

  useEffect(() => {
    if (!active) {
      setIsEditingCustomProxy(false);
      return;
    }

    if (isLocalhostEnv) {
      proxyService.repairStoredProxyUrl();
    }
    setProxyMode(proxyService.getProxyMode());
    setCustomProxyUrl(
      isLocalhostEnv
        ? 'http://localhost:9000'
        : proxyService.resolveProxyUrlForSave(proxyService.getDesktopAgentUrl())
    );
    setIsEditingCustomProxy(false);
    const savedTimeout = msToTimeoutDisplay(proxyService.getRequestTimeoutMs());
    setRequestTimeoutValue(String(savedTimeout.value));
    setRequestTimeoutUnit(savedTimeout.unit);
    setRequestTimeoutError('');
    testProxyConnection();
  }, [active]);

  const handleProxyModeChange = (mode: 'auto' | 'custom') => {
    setProxyMode(mode);
    proxyService.setProxyMode(mode);
    if (mode === 'custom') {
      setIsEditingCustomProxy(false);
    }
    void testProxyConnection();
  };

  const testProxyConnection = async (): Promise<boolean> => {
    setTestInProgress(true);
    setProxyStatus('checking');
    setStatusMessage('Testing proxy connection...');

    try {
      const result = await proxyService.testProxyConnection();

      if (result.success) {
        setProxyStatus('available');
        setStatusMessage(result.message || 'Proxy server is running and accessible');
        void refreshProxyStatus();
        return true;
      }

      setProxyStatus('unavailable');
      setStatusMessage(result.message || 'Proxy server is not responding');
      return false;
    } catch (error: any) {
      setProxyStatus('unavailable');
      setStatusMessage(`Failed to connect: ${error.message}`);
      return false;
    } finally {
      setTestInProgress(false);
    }
  };

  const handleSave = async () => {
    const timeoutResult = timeoutInputToMs(requestTimeoutValue, requestTimeoutUnit);
    if (!timeoutResult.ok) {
      setRequestTimeoutError(timeoutResult.error);
      setStatusMessage(timeoutResult.error);
      return;
    }
    setRequestTimeoutError('');

    proxyService.setProxyMode(proxyMode);
    if (proxyMode === 'custom') {
      try {
        const valueToSave = isLocalhostEnv
          ? 'http://localhost:9000'
          : proxyService.resolveProxyUrlForSave(customProxyUrl);
        new URL(valueToSave);
        proxyService.setProxyUrl(valueToSave);
      } catch {
        setStatusMessage('Invalid URL format. Please enter a valid URL (e.g., http://localhost:9000)');
        return;
      }
    }

    try {
      proxyService.setRequestTimeoutMs(timeoutResult.ms);
    } catch (e: any) {
      const message = e?.message || 'Timeout must be greater than 0.';
      setRequestTimeoutError(message);
      setStatusMessage(message);
      return;
    }

    setIsEditingCustomProxy(false);

    const isAvailable = await testProxyConnection();
    if (isAvailable) {
      await refreshProxyStatus();
      onSaveSuccess?.();
    }
  };

  const handleDownload = async (platform: string) => {
    let fileName = '';

    switch (platform) {
      case 'windows':
        fileName = 'crud-expert-proxy-setup.exe';
        break;
      case 'mac-dmg':
        fileName = 'curd-expert-proxy.dmg';
        break;
      case 'mac-intel':
        fileName = 'curd-expert-proxy-mac-intel';
        break;
      case 'mac-arm':
        fileName = 'curd-expert-proxy-mac-arm';
        break;
      case 'linux':
        fileName = 'curd-expert-proxy-linux';
        break;
    }

    if (!fileName) return;

    if (platform === 'windows') {
      try {
        const link = document.createElement('a');
        link.href = `/downloads/${fileName}?v=${WINDOWS_PROXY_INSTALLER_VERSION}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      } catch (e) {
        window.alert(extractApiErrorMessage(e) || 'Download failed. Try again or contact support.');
        return;
      }
    }

    try {
      await apiService.downloadLocalProxyArtifact(platform);
      return;
    } catch {
      /* fall back to static /downloads when API is unavailable or artifacts dir is not configured */
    }

    try {
      const link = document.createElement('a');
      link.href = `/downloads/${fileName}`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      window.alert(extractApiErrorMessage(e) || 'Download failed. Try again or contact support.');
    }
  };

  const showInstallInstructions = (platform: string) => {
    let title = '';
    let content = '';

    switch (platform) {
      case 'windows':
        title = 'Windows Installer';
        content = `Standard Windows setup wizard with the CRUD logo.\nInstalls the CRUD Expert desktop agent as a hidden background service — no CMD window. Auto-starts after reboot.\n\n1. Run crud-expert-proxy-setup.exe\n\n2. Approve UAC (Administrator)\n\n3. Setup wizard: Next → Install → Finish\n\n4. If SmartScreen appears: More info → Run anyway\n\n5. Installs to: C:\\Program Files\\CrudExpert\\ProxyAgent\n\n6. Desktop agent runs on http://localhost:9000 — return to Proxy Settings → Save & Test\n\nUninstall: Settings → Apps → CRUD Expert Proxy → Uninstall\n\n(Legacy console installer is no longer offered.)`;
        break;
      case 'mac-dmg':
        title = 'macOS DMG Installer Instructions';
        content = `Installs the CRUD Expert desktop agent as a background Mac app (LaunchAgent).\nNo Terminal. Starts automatically after you log in.\n\n1. Open the downloaded curd-expert-proxy.dmg\n\n2. Double-click "crud-expert-proxy.app"\n\n3. If macOS says it cannot be opened:\n   • Right-click "crud-expert-proxy.app"\n   • Choose Open\n   • Click Open again\n\n4. Wait for the notification — the desktop agent is running on http://localhost:9000\n\n5. Return to Proxy Settings → Save & Test (URL: http://localhost:9000)\n\nThe app appears in Applications as crud-expert-proxy.app with the CRUD logo.\n\nAlready have it running? Just set Proxy Settings to http://localhost:9000 and Save & Test.`;
        break;
      case 'mac-intel':
        title = 'macOS Intel Portable Instructions';
        content = `1. Open Terminal (Applications → Utilities → Terminal)\n\n2. Navigate to Downloads:\n   cd ~/Downloads\n\n3. Make the file executable:\n   chmod +x curd-expert-proxy-mac-intel\n\n4. Run the CRUD Expert desktop agent:\n   ./curd-expert-proxy-mac-intel\n\n5. You should see:\n   🚀 CRUD Expert desktop agent is running!\n   📍 Server URL: http://localhost:9000\n\n6. Keep the Terminal window open while testing\n\n7. Return to Proxy Settings and configure:\n   • Select "Custom Proxy URL"\n   • Enter: http://localhost:9000\n   • Click "Save & Test"\n\nTo stop: Press Ctrl+C in the Terminal`;
        break;
      case 'mac-arm':
        title = 'macOS ARM (M1/M2/M3) Instructions';
        content = `1. Open Terminal (Applications → Utilities → Terminal)\n\n2. Navigate to Downloads:\n   cd ~/Downloads\n\n3. Make the file executable:\n   chmod +x curd-expert-proxy-mac-arm\n\n4. Run the CRUD Expert desktop agent:\n   ./curd-expert-proxy-mac-arm\n\n5. You should see:\n   🚀 CRUD Expert desktop agent is running!\n   📍 Server URL: http://localhost:9000\n\n6. Keep the Terminal window open while testing\n\n7. Return to Proxy Settings and configure:\n   • Select "Custom Proxy URL"\n   • Enter: http://localhost:9000\n   • Click "Save & Test"\n\nTo stop: Press Ctrl+C in the Terminal`;
        break;
      case 'linux':
        title = 'Linux Installation Instructions';
        content = `1. Open Terminal\n\n2. Navigate to Downloads:\n   cd ~/Downloads\n\n3. Make the file executable:\n   chmod +x curd-expert-proxy-linux\n\n4. Run the CRUD Expert desktop agent:\n   ./curd-expert-proxy-linux\n\n5. You should see:\n   🚀 CRUD Expert desktop agent is running!\n   📍 Server URL: http://localhost:9000\n\n6. Keep the Terminal window open while testing\n\n7. Return to Proxy Settings and configure:\n   • Select "Custom Proxy URL"\n   • Enter: http://localhost:9000\n   • Click "Save & Test"\n\nOptional - Install system-wide:\n   sudo mv curd-expert-proxy-linux /usr/local/bin/curd-expert-proxy\n   sudo chmod +x /usr/local/bin/curd-expert-proxy\n\nThen run from anywhere: curd-expert-proxy\n\nTo stop: Press Ctrl+C in the Terminal`;
        break;
    }

    setCurrentInstructions({ title, content });
    setShowInstructions(true);
  };

  const getStatusIcon = () => {
    switch (proxyStatus) {
      case 'available':
        return <CheckCircleIcon color="success" />;
      case 'unavailable':
        return <ErrorIcon color="error" />;
      default:
        return <RefreshIcon />;
    }
  };

  const getStatusColor = () => {
    switch (proxyStatus) {
      case 'available':
        return 'success';
      case 'unavailable':
        return 'error';
      default:
        return 'info';
    }
  };

  const footerActions =
    footer === 'none' ? null : (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          flexWrap: 'wrap',
          pt: footer === 'page' ? 1.5 : 0,
          mt: footer === 'page' ? 0.5 : 0,
          borderTop: footer === 'page' ? `1px solid ${DFT_BRAND.line}` : 'none',
        }}
      >
        {footer === 'dialog' && onCancel && (
          <Button onClick={onCancel} sx={proxyBtnSx}>Cancel</Button>
        )}
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ ...proxyBtnSx, bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}
        >
          Save & Test
        </Button>
      </Box>
    );

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
        {showHeader && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DFT_BRAND.text, m: 0 }}>
              Proxy Server Settings
            </Typography>
            <Chip
              icon={getStatusIcon()}
              label={proxyStatus === 'checking' ? 'Checking...' : proxyStatus.toUpperCase()}
              color={getStatusColor() as 'success' | 'error' | 'info'}
              size="small"
              sx={{ height: 24, flexShrink: 0 }}
            />
          </Box>
        )}
        {!showHeader && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
            <Chip
              icon={getStatusIcon()}
              label={proxyStatus === 'checking' ? 'Checking...' : proxyStatus.toUpperCase()}
              color={getStatusColor() as 'success' | 'error' | 'info'}
              size="small"
              sx={{ height: 24, flexShrink: 0 }}
            />
          </Box>
        )}

        <FormControl component="fieldset">
          <Typography variant="subtitle1" gutterBottom fontWeight={700} sx={{ fontSize: '0.9375rem', color: DFT_BRAND.text }}>
            Proxy Configuration
          </Typography>
          <RadioGroup
            value={proxyMode}
            onChange={(e) => handleProxyModeChange(e.target.value as 'auto' | 'custom')}
          >
            <FormControlLabel
              value="custom"
              control={<Radio size="small" />}
              sx={{ alignItems: 'flex-start', mr: 0, ml: 0 }}
              label={
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Custom Proxy URL — CRUD Expert desktop agent (Recommended)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                    Manually specify the proxy server address
                  </Typography>
                </Box>
              }
            />
            {proxyMode === 'custom' && (
              <Box sx={{ pl: { xs: 4, sm: 4.5 }, pr: 0, mt: 1, mb: 1.25, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <TextField
                    label="Proxy Server URL"
                    value={customProxyUrl}
                    size="small"
                    onChange={(e) => {
                      if (isEditingCustomProxy) setCustomProxyUrl(e.target.value);
                    }}
                    InputProps={{ readOnly: !isEditingCustomProxy }}
                    fullWidth
                    placeholder="http://localhost:9000"
                    helperText={
                      isLocalhostEnv
                        ? 'Read-only until you click Edit. Save uses http://localhost:9000 for requests.'
                        : 'Read-only until you click Edit. Then enter the full URL (http:// or https://) and Save & Test.'
                    }
                    variant="outlined"
                    sx={{
                      flex: '1 1 240px',
                      minWidth: 0,
                      '& .MuiOutlinedInput-root': { height: 40, fontSize: '0.875rem' },
                    }}
                  />
                  {!isEditingCustomProxy ? (
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => setIsEditingCustomProxy(true)}
                      sx={{ ...proxyBtnSx, mt: { xs: 0, sm: 0.25 }, flexShrink: 0 }}
                    >
                      Edit
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outlined"
                      color="inherit"
                      onClick={() => {
                        setIsEditingCustomProxy(false);
                        const reverted = proxyService.getProxyUrl();
                        setCustomProxyUrl(
                          isLocalhostEnv ? 'http://localhost:9000' : reverted || 'http://localhost:9000'
                        );
                      }}
                      sx={{ ...proxyBtnSx, mt: { xs: 0, sm: 0.25 }, flexShrink: 0 }}
                    >
                      Cancel edit
                    </Button>
                  )}
                  <Tooltip title="Test Connection">
                    <IconButton
                      size="small"
                      onClick={testProxyConnection}
                      disabled={testInProgress}
                      sx={{ width: 32, height: 32, mt: { xs: 0, sm: 0.5 }, flexShrink: 0 }}
                    >
                      <RefreshIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                {statusMessage && (
                  <Typography
                    variant="body2"
                    color={proxyStatus === 'available' ? 'success.main' : 'error.main'}
                    sx={{ mt: 0.75, display: 'block', fontSize: '0.8125rem' }}
                  >
                    {statusMessage}
                  </Typography>
                )}
              </Box>
            )}
            <FormControlLabel
              value="auto"
              control={<Radio size="small" />}
              sx={{ alignItems: 'flex-start', mr: 0, ml: 0, mt: 0.5 }}
              label={
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Server Proxy</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                    Automatically uses localhost:9000 for local development, or server proxy for deployed apps
                  </Typography>
                </Box>
              }
            />
            {proxyMode === 'auto' && (
              <Box sx={{ pl: { xs: 4, sm: 4.5 }, pr: 0, mt: 1, mb: 0.5, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <TextField
                    label="Proxy Server URL"
                    value={proxyService.getServerProxyUrl()}
                    size="small"
                    InputProps={{ readOnly: true }}
                    fullWidth
                    helperText="Requests are sent through the CRUD Expert backend proxy."
                    variant="outlined"
                    sx={{
                      flex: '1 1 240px',
                      minWidth: 0,
                      '& .MuiOutlinedInput-root': { height: 40, fontSize: '0.875rem' },
                    }}
                  />
                  <Tooltip title="Test Connection">
                    <IconButton
                      size="small"
                      onClick={testProxyConnection}
                      disabled={testInProgress}
                      sx={{ width: 32, height: 32, mt: { xs: 0, sm: 0.5 }, flexShrink: 0 }}
                    >
                      <RefreshIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                {statusMessage && (
                  <Typography
                    variant="body2"
                    color={proxyStatus === 'available' ? 'success.main' : 'error.main'}
                    sx={{ mt: 0.75, display: 'block', fontSize: '0.8125rem' }}
                  >
                    {statusMessage}
                  </Typography>
                )}
              </Box>
            )}
          </RadioGroup>
        </FormControl>

        <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 0 }}>
          <Typography variant="body2">
            <strong>Why two proxy servers?</strong>
            <br />
            • <strong>Server Proxy:</strong> For testing APIs hosted on servers (accessible remotely)
            <br />
            • <strong>CRUD Expert desktop agent:</strong> For testing localhost APIs on your machine
          </Typography>
        </Alert>

        <Box>
          <Typography variant="subtitle1" gutterBottom fontWeight={700} sx={{ fontSize: '0.9375rem', color: DFT_BRAND.text }}>
            Request Timeout
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              label="Request Timeout"
              type="number"
              size="small"
              value={requestTimeoutValue}
              onChange={(e) => {
                setRequestTimeoutValue(e.target.value);
                if (requestTimeoutError) setRequestTimeoutError('');
              }}
              inputProps={{
                min: 1,
                step: 1,
                max: requestTimeoutUnit === 'minutes' ? 5 : 300,
              }}
              error={!!requestTimeoutError}
              helperText={
                requestTimeoutError ||
                'How long to wait for the target API before failing the request. Maximum 5 minutes.'
              }
              variant="outlined"
              sx={{
                width: { xs: '100%', sm: 180 },
                '& .MuiOutlinedInput-root': { height: 40, boxSizing: 'border-box', fontSize: '0.875rem' },
              }}
            />
            <FormControl
              variant="outlined"
              size="small"
              sx={{
                width: { xs: '100%', sm: 180 },
                alignSelf: 'flex-start',
                '& .MuiOutlinedInput-root': { height: 40, boxSizing: 'border-box', fontSize: '0.875rem' },
              }}
            >
              <InputLabel id="request-timeout-unit-label">Unit</InputLabel>
              <Select
                labelId="request-timeout-unit-label"
                label="Unit"
                value={requestTimeoutUnit}
                open={timeoutUnitZoomMenu.menuOpen}
                onOpen={timeoutUnitZoomMenu.onOpen}
                onClose={timeoutUnitZoomMenu.onClose}
                MenuProps={timeoutUnitZoomMenu.MenuProps}
                onChange={(e) => {
                  const nextUnit = e.target.value as RequestTimeoutUnit;
                  setRequestTimeoutUnit(nextUnit);
                  const n = Number(String(requestTimeoutValue).trim());
                  if (!Number.isFinite(n)) return;
                  const maxValue = nextUnit === 'minutes' ? 5 : 300;
                  if (n > maxValue) {
                    setRequestTimeoutValue(String(maxValue));
                    setRequestTimeoutError('');
                  }
                }}
              >
                <MenuItem value="seconds">Seconds</MenuItem>
                <MenuItem value="minutes">Minutes</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Paper elevation={0} sx={{ ...proxyCardSx, p: 1.5 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={700} sx={{ fontSize: '0.9375rem', color: DFT_BRAND.text }}>
            Download CRUD Expert Desktop Agent
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Choose the installer for your system (Windows: crud-expert-proxy-setup.exe, macOS: crud-expert-proxy.dmg)
          </Typography>

          <Box sx={{ mt: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 220px' }, minWidth: 0 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WindowsIcon fontSize="small" color="primary" />
                <Typography variant="body2" fontWeight="medium">
                  Windows
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
                <Box display="flex" gap={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload('windows')}
                    sx={PROXY_DOWNLOAD_BTN_SX}
                  >
                    Windows Installer
                  </Button>
                  <Tooltip title="Installation Instructions">
                    <IconButton size="small" onClick={() => showInstallInstructions('windows')} sx={{ width: 32, height: 32, flexShrink: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, maxWidth: '100%' }}>
                  One download (crud-expert-proxy-setup.exe). Installs the desktop agent as a background Windows service.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 220px' }, minWidth: 0 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AppleIcon fontSize="small" color="primary" />
                <Typography variant="body2" fontWeight="medium">
                  macOS
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
                <Box display="flex" gap={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload('mac-dmg')}
                    sx={PROXY_DOWNLOAD_BTN_SX}
                  >
                    Installer (DMG)
                  </Button>
                  <Tooltip title="Installation Instructions">
                    <IconButton size="small" onClick={() => showInstallInstructions('mac-dmg')} sx={{ width: 32, height: 32, flexShrink: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, maxWidth: '100%' }}>
                  Recommended: crud-expert-proxy.dmg — background Mac app, no Terminal.
                </Typography>
                <Box display="flex" gap={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload('mac-intel')}
                    sx={PROXY_DOWNLOAD_BTN_SX}
                  >
                    Intel (Portable)
                  </Button>
                  <Tooltip title="Installation Instructions">
                    <IconButton size="small" onClick={() => showInstallInstructions('mac-intel')} sx={{ width: 32, height: 32, flexShrink: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box display="flex" gap={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload('mac-arm')}
                    sx={PROXY_DOWNLOAD_BTN_SX}
                  >
                    ARM (M1/M2/M3)
                  </Button>
                  <Tooltip title="Installation Instructions">
                    <IconButton size="small" onClick={() => showInstallInstructions('mac-arm')} sx={{ width: 32, height: 32, flexShrink: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 220px' }, minWidth: 0 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LinuxIcon fontSize="small" color="primary" />
                <Typography variant="body2" fontWeight="medium">
                  Linux
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
                <Box display="flex" gap={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload('linux')}
                    sx={PROXY_DOWNLOAD_BTN_SX}
                  >
                    64-bit (Executable)
                  </Button>
                  <Tooltip title="Installation Instructions">
                    <IconButton size="small" onClick={() => showInstallInstructions('linux')} sx={{ width: 32, height: 32, flexShrink: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Box>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: DFT_BRAND.text }}>
            Quick Settings
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              label="Local (localhost:9000)"
              onClick={() => {
                setProxyMode('custom');
                setCustomProxyUrl('http://localhost:9000');
                setIsEditingCustomProxy(true);
              }}
              clickable
              size="small"
            />
            <Chip
              label="Local (127.0.0.1:9000)"
              onClick={() => {
                setProxyMode('custom');
                setCustomProxyUrl('http://127.0.0.1:9000');
                setIsEditingCustomProxy(true);
              }}
              clickable
              size="small"
            />
            <Chip
              label="Server Proxy"
              onClick={() => {
                handleProxyModeChange('auto');
              }}
              clickable
              size="small"
            />
          </Box>
        </Box>

        {footerActions}
      </Box>

      <Dialog
        open={showInstructions}
        onClose={() => setShowInstructions(false)}
        maxWidth={false}
        scroll="paper"
        PaperProps={{
          sx: {
            ...proxyDialogPaperSx,
            width: { xs: 'calc(100% - 32px)', sm: 520 },
            maxHeight: 'calc(100vh - 160px)',
          },
        }}
      >
        <DialogTitle sx={{ flexShrink: 0, pb: 1.25, fontWeight: 700, fontSize: '1.05rem' }}>
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <InfoIcon sx={{ color: '#1d4ed8', fontSize: 20, flexShrink: 0 }} />
            <Typography variant="subtitle1" component="span" sx={{ fontWeight: 700, color: DFT_BRAND.text }}>
              {currentInstructions.title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0, px: 2.5, py: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              lineHeight: 1.6,
              fontSize: '0.875rem',
              m: 0,
            }}
          >
            {currentInstructions.content}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, px: 2, pb: 1.5 }}>
          <Button
            onClick={() => setShowInstructions(false)}
            variant="contained"
            sx={{ ...proxyBtnSx, bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProxySettingsContent;
